import { Size } from '~/types/ThemeTypes'
import {
  createMultiStyleConfigHelpers,
  MultiStyleType,
  StyleThemingProps,
} from '~/utils/chakraUtils'
import { mergeMultiStyleConfig } from '~/utils/styleHelpers'

import { textStyleKeys } from '@mangovoice/design-system'

// MissedCallsListItemStyle
const missedCallsListItemStyleParts = [
  'container',
  'rowWrapper',
  'dateTimeColumn',
  'toColumn',
  'fromColumn',
  'followUpTimeColumn',
  'typeColumn',
  'followUpStatusColumn',
  'assignedToColumn',
  'actionColumn',
  'timeWrapper',
  'timeText',
  'dateText',
  'titleContainer',
  'titleText',
  'actionText',
] as const

const helpers = createMultiStyleConfigHelpers(missedCallsListItemStyleParts)

export const MissedCallsListItemStyle = helpers.defineMultiStyleConfig({
  baseStyle: {
    container: {
      display: 'flex',
      height: '4.75rem',
      backgroundColor: 'bg.subtle',
      px: '1rem',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: '0.625rem',
      alignSelf: 'stretch',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      border: '1px solid',
      borderColor: 'transparent',
      borderBottom: '1px solid',
      borderBottomColor: 'border.default',
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
      _hover: {
        borderColor: 'border.muted',
        borderBottomColor: 'border.default',
        bg: 'bg.muted',
        '& > div': {
          borderBottomColor: 'transparent',
        },
      },
      _selected: {
        borderColor: 'border.muted',
        borderBottomColor: 'border.default',
        bg: 'bg.emphasized',
      },
    },
    followUpTimeColumn: {
      display: 'flex',
      height: '3.75rem',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'flex-start',
      flex: '1 0 0',
    },
    rowWrapper: {
      display: 'flex',
      alignItems: 'center',
      flex: '1 0 0',
      alignSelf: 'stretch',
    },
    dateTimeColumn: {
      display: 'flex',
      pr: '1rem',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'flex-start',
      flex: '1 0 0',
    },
    timeWrapper: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem',
    },
    timeText: {
      textStyle: textStyleKeys.Body1,
    },
    dateText: {
      textStyle: textStyleKeys.Body3,
      color: 'fg.muted',
    },
    toColumn: {
      display: 'flex',
      height: '3.75rem',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'flex-start',
      flex: '1 0 0',
    },
    fromColumn: {
      display: 'flex',
      height: '3.75rem',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'flex-start',
      flex: '1 0 0',
    },
    typeColumn: {
      display: 'flex',
      height: '3.75rem',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'flex-start',
      flex: '1 0 0',
    },
    followUpStatusColumn: {
      display: 'flex',
      height: '3.75rem',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'flex-start',
      flex: '1 0 0',
    },
    assignedToColumn: {
      display: 'flex',
      height: '3.75rem',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'flex-start',
      flex: '1 0 0',
    },
    actionColumn: {
      display: 'flex',
      height: '3.75rem',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'flex-start',
      flex: '0 1 0',
    },
    titleContainer: {
      display: 'flex',
      height: '1.75rem',
      alignItems: 'center',
      gap: '0.5rem',
      pr: '1rem',
      flexShrink: 0,
      alignSelf: 'stretch',
    },
    titleText: {
      textStyle: textStyleKeys.Body1,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    actionText: {
      textStyle: textStyleKeys.Body1,
      color: 'fg.muted',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  sizes: {
    [Size.sm]: {
      container: {
        px: '0.75rem',
      },
    },
  },
})

export const useMissedCallsListItemStyles = (
  props?: StyleThemingProps,
): MultiStyleType<typeof missedCallsListItemStyleParts> => {
  return mergeMultiStyleConfig(
    MissedCallsListItemStyle,
    props,
  ) as MultiStyleType<typeof missedCallsListItemStyleParts>
}
